/* Indie Reader push tab — Feb-2026.
 *
 * Rendered inside `BookEditor` as a second tab. Shows:
 *   • cover preview,
 *   • 3–5 sample-chapter slider,
 *   • live status chip (pending / assigned / approved / rejected …),
 *   • submit button + history of prior review rows.
 *
 * Author (i.e. the admin editing the book) submits a REVIEW row here.
 * An editor (see /admin → Users → Reader Review Queue) actually
 * decides whether it goes upstream to the Companion app.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, Send, BookOpen, Clock3, CheckCircle2, XCircle, AlertTriangle,
  History,
} from 'lucide-react';
import { resolveCoverUrl } from '@/lib/bookstoreApi';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const http = axios.create({ baseURL: API, withCredentials: true });

const STATUS_TONE = {
  pending:            { label: 'Pending',           icon: Clock3,       tone: 'text-amber-700 bg-amber-500/15 border-amber-500/40'   },
  assigned:           { label: 'Assigned',          icon: Clock3,       tone: 'text-sky-700   bg-sky-500/15   border-sky-500/40'     },
  approved:           { label: 'Approved',          icon: CheckCircle2, tone: 'text-emerald-700 bg-emerald-500/15 border-emerald-500/40' },
  changes_requested:  { label: 'Changes requested', icon: AlertTriangle,tone: 'text-orange-700 bg-orange-500/15 border-orange-500/40' },
  rejected:           { label: 'Rejected',          icon: XCircle,      tone: 'text-rose-700   bg-rose-500/15   border-rose-500/40'   },
  cancelled:          { label: 'Cancelled',         icon: XCircle,      tone: 'text-muted-foreground bg-secondary border-border'      },
};

function StatusChip({ status }) {
  const s = STATUS_TONE[status] || {
    label: status || 'Not submitted', icon: BookOpen,
    tone: 'text-muted-foreground bg-secondary border-border',
  };
  const Icon = s.icon;
  return (
    <span data-testid={`irtab-status-chip-${status || 'none'}`}
          className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider ${s.tone}`}>
      <Icon className="h-3 w-3" /> {s.label}
    </span>
  );
}

export default function IndieReaderTab({ book }) {
  const [samples,    setSamples]    = useState(3);
  const [force,      setForce]      = useState(false);
  const [busy,       setBusy]       = useState(false);
  const [state,      setState]      = useState({ latest: null, history: [], open: false });
  const [loading,    setLoading]    = useState(true);

  const refresh = useCallback(async () => {
    if (!book?.id) { setLoading(false); return; }
    setLoading(true);
    try {
      const r = await http.get(`/admin/reader-review/book/${book.id}`);
      setState(r.data);
      if (r.data?.latest?.sample_chapter_count) {
        setSamples(r.data.latest.sample_chapter_count);
      }
    } catch (_e) {
      // Non-fatal — history simply won't render.
    } finally { setLoading(false); }
  }, [book?.id]);

  useEffect(() => { refresh(); }, [refresh]);

  const submit = async () => {
    if (!book?.id) return;
    setBusy(true);
    try {
      await http.post(`/admin/reader-review/submit/${book.id}`, {
        sample_chapter_count: samples, force,
      });
      toast.success('Submitted for editor review.');
      await refresh();
    } catch (e) {
      const detail = e.response?.data?.detail || 'Submission failed.';
      toast.error(detail);
    } finally { setBusy(false); }
  };

  const cancel = async (reviewId) => {
    if (!window.confirm('Cancel the pending review request?')) return;
    try {
      await http.post(`/admin/reader-review/${reviewId}/cancel`);
      toast.success('Cancelled.');
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Cancel failed.');
    }
  };

  const previewUrl = resolveCoverUrl(book?.cover_thumb_url || book?.cover_front_url);
  const latest     = state.latest;
  const canSubmit  = book?.id && !state.open && !busy;

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-3" data-testid="ir-push-tab">
      {/* Cover preview column */}
      <div className="md:col-span-1">
        <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Cover · what the Reader sees</div>
        <div className="mt-1 aspect-[2/3] w-full overflow-hidden rounded-md border border-border bg-secondary"
             data-testid="ir-push-cover-preview">
          {previewUrl
            ? <img src={previewUrl} alt="cover" className="h-full w-full object-cover" />
            : <div className="flex h-full w-full items-center justify-center text-[11px] text-muted-foreground">
                No cover uploaded
              </div>}
        </div>
        <div className="mt-2 text-[11.5px] leading-relaxed text-muted-foreground">
          The Reader shows this cover + the first {samples} chapter{samples === 1 ? '' : 's'}
          of your sample text, back-cover copy, and heat / trope chips.
        </div>
      </div>

      {/* Controls + status column */}
      <div className="md:col-span-2 space-y-4">
        <div className="rounded-md border border-border bg-card p-4">
          <div className="flex items-center justify-between">
            <div className="text-[13px] font-semibold">Current status</div>
            {loading
              ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              : <StatusChip status={latest?.status} />}
          </div>
          {latest?.decision_notes && (
            <div className="mt-2 rounded-md border border-dashed border-border bg-secondary/40 p-2 text-[12px]">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Editor notes</div>
              <div className="mt-1 whitespace-pre-wrap" data-testid="ir-push-decision-notes">
                {latest.decision_notes}
              </div>
            </div>
          )}
          {latest?.push_error && (
            <div className="mt-2 rounded-md border border-rose-500/30 bg-rose-500/10 p-2 text-[12px] text-rose-700">
              Push failed after approval: {latest.push_error}
            </div>
          )}
        </div>

        <div className="rounded-md border border-border bg-card p-4">
          <div className="text-[13px] font-semibold">Submit for editor review</div>
          <div className="mt-1 text-[11.5px] text-muted-foreground">
            The editor reviews the sample chapters and approves the push to
            Indie Reader, or blocks with notes.
          </div>
          <div className="mt-3 flex items-end gap-3">
            <label className="flex-1">
              <span className="mb-1 block text-[11px] text-muted-foreground">
                Sample chapters · <b>{samples}</b>
              </span>
              <input
                type="range" min={3} max={5} step={1} value={samples}
                onChange={(e) => setSamples(parseInt(e.target.value, 10) || 3)}
                disabled={state.open}
                data-testid="ir-push-sample-slider"
                className="w-full accent-primary"
              />
              <div className="mt-0.5 flex justify-between text-[10px] text-muted-foreground">
                <span>3</span><span>4</span><span>5</span>
              </div>
            </label>
            <label className="inline-flex items-center gap-1.5 pb-1 text-[11.5px]">
              <input type="checkbox" checked={force}
                     onChange={(e) => setForce(e.target.checked)}
                     disabled={state.open}
                     data-testid="ir-push-force-toggle" />
              Force fresh push
            </label>
          </div>

          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              disabled={!canSubmit}
              onClick={submit}
              data-testid="ir-push-submit-btn"
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-[12px] font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
              Submit for review
            </button>
            {state.open && latest && (
              <button type="button" onClick={() => cancel(latest.id)}
                      data-testid="ir-push-cancel-btn"
                      className="rounded-md border border-border px-3 py-2 text-[12px] hover:bg-secondary">
                Cancel pending request
              </button>
            )}
            {!book?.id && (
              <span className="text-[11px] text-muted-foreground">
                Save the book first, then submit.
              </span>
            )}
          </div>
        </div>

        {/* History strip */}
        <div className="rounded-md border border-border bg-card p-4">
          <div className="mb-2 inline-flex items-center gap-1.5 text-[12px] font-semibold">
            <History className="h-3.5 w-3.5" /> Review history
          </div>
          {state.history?.length ? (
            <ul className="divide-y divide-border/60" data-testid="ir-push-history">
              {state.history.map((r) => (
                <li key={r.id} className="flex items-start justify-between gap-2 py-2 text-[11.5px]">
                  <div className="flex-1">
                    <div className="flex items-center gap-1.5">
                      <StatusChip status={r.status} />
                      <span className="text-muted-foreground">· submitted {(r.submitted_at || '').slice(0, 10)}</span>
                    </div>
                    <div className="mt-0.5 text-muted-foreground">
                      by {r.submitted_by}
                      {r.assigned_to && <> · assigned to <b className="text-foreground">{r.assigned_to}</b></>}
                      {r.decided_by && <> · decided by <b className="text-foreground">{r.decided_by}</b></>}
                      {' · '}
                      {r.sample_chapter_count} sample ch.
                    </div>
                    {r.decision_notes && (
                      <div className="mt-1 rounded border border-dashed border-border bg-secondary/40 p-1.5">
                        {r.decision_notes}
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-[11.5px] text-muted-foreground">No review history yet.</div>
          )}
        </div>
      </div>
    </div>
  );
}
